/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */


import {
  Component,
  EmbeddedViewRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';

import {TranslateService} from '@ngx-translate/core';

import {forkJoin, Observable, of} from 'rxjs';
import {finalize, map, switchMap, tap} from 'rxjs/operators';

import * as moment from 'moment/moment';

import {ToastrService} from 'ngx-toastr';
import {AbsenceEvent} from '../../+payrolls/prepare/time/calendar/calendar-absence/calendar-absence.component';
import {TimeAndAttendanceService} from '../../+payrolls/prepare/time/time-and-attendance.service';
import {DATE_FORMAT, ProfileMode} from '../../app.constants';
import {Destroyable} from '../../destroyable.component';
import {CliEmpAttributes} from '../../models/models.interfaces';
import {DateRange, PayCalendarDayAttributes} from '../../models/time-and-attendance.interfaces';
import {Data} from '../../shared/api';
import {Validators as GpValidators} from '../../shared/forms';
import {ModalService} from '../../shared/modal';
import {AvailabilityValues, DayWrapper, MappedDayWrapper, Team} from '../../shared/teams/teams.model';
import {TeamsService} from '../../shared/teams/teams.service';
import {User, UserService} from '../../shared/user';
import {range} from '../../shared/utils/date';
import Moment = moment.Moment;

const weekOfYear = (m: Moment): number => m.week();

@Component({
  selector: 'team-planning',
  templateUrl: 'team-planning.component.html',
  styleUrls: [
    '../../../assets/fullcalendar/fullcalendar.theme.scss',
    'team-planning.component.scss'
  ]
})
export class TeamPlanningComponent extends Destroyable implements OnInit {

  public static ACCESS_NAME: string = 'TeamPlanning';

  public reduced: boolean = false;

  @Input()
  public displayIncomplete: boolean = false;

  @Input()
  public currentDate: Moment = moment();

  @Input()
  public isIframe: boolean = false;

  @Input()
  public selectedTeam: number = 0;

  @Input()
  public profileMode: ProfileMode;

  public teamName: UntypedFormControl = new UntypedFormControl('', [Validators.required]);
  public employees: Array<Data<CliEmpAttributes>> = [];
  public loading: boolean = false;
  public page: number = 0;
  public toggleSeeInactiveContracts: boolean = false;
  public today: Date = moment().startOf('day').toDate();

  public isLoading: boolean = false;
  public dataLoaded: EventEmitter<void> = new EventEmitter<void>();

  public teams: Team[] = [];
  public workCycle: MappedDayWrapper[][] = [];
  public activeMenu: EmbeddedViewRef<Data<CliEmpAttributes>>;
  public userId: number;

  public teamAvalabilityMap: Map<string, AvailabilityValues> = new Map<string, AvailabilityValues>();
  public absenceMap: Map<string, AbsenceEvent[]> = new Map<string, AbsenceEvent[]>();
  public availablityValues: { percent: number; people: number }[] = [];

  public dayLabels: string[] = [];
  public monthRange1Week: string;
  public monthRange2Week: string;
  public week1Num: number;
  public week2Num: number;
  public minDate = moment(this.currentDate).add(-1, 'months').toDate();
  public maxDate = moment(this.currentDate).add(1, 'years').toDate();

  public eventForm: UntypedFormGroup;
  public isBeginBeforeEnd: boolean = true;
  public currentEmployee: Data<CliEmpAttributes>;
  public isSliderLoaded: boolean = false;
  public absenceEvent: any;
  public timeFields: any;
  public workcycleMap: Map<string, any> = new Map<string, any>();
  public searchTerm = '';
  public isCustomTeam = false;
  public newTeamMemberId: number;

  public formatter: any = {
    from(value: string): number {
      return +value;
    },
    to(value: number): string {
      return `${value}`;
    }
  };

  @ViewChild('employeeList', {read: ViewContainerRef})
  public employeeList: ViewContainerRef;


  @ViewChild('submenu')
  public submenuTpl: TemplateRef<Data<CliEmpAttributes>>;

  @Input()
  public isEmployeeActive: Function = () => {
    // fake function
  }

  @HostListener('window:click') outsideClick() {
    if (this.activeMenu) {
      this.activeMenu.destroy();
    }
  }

  set singleDateRange(dateRange: any) {
    this.setDateRangeTime('singleDateRange', dateRange, 'begin');
  }

  set beginDateRange(dateRange: any) {
    this.setDateRangeTime('beginDateRange', dateRange, 'begin');
  }

  set endDateRange(dateRange: any) {
    this.setDateRangeTime('endDateRange', dateRange, 'end');
  }

  get singleDateRange() {
    return this.absenceEvent.singleDateRange;
  }

  get beginDateRange() {
    return this.absenceEvent.beginDateRange;
  }

  get endDateRange() {
    return this.absenceEvent.endDateRange;
  }

  constructor(public formBuilder: UntypedFormBuilder,
              private teamsService: TeamsService,
              private translate: TranslateService,
              private modalService: ModalService,
              private viewContainer: ViewContainerRef,
              private timeAndAttendenceService: TimeAndAttendanceService,
              private toastrService: ToastrService,
              private userService: UserService) {
    super();
  }

  public ngOnInit(): void {
    this.setDateLabels();
    this.initAbsenceWindow();
    this.createForm();
    this.translate.onLangChange.subscribe(() => {
      this.currentDate = moment();
      this.setDateLabels();
    });

    this.userService.getUser()
      .pipe(
        tap((user: User) => this.userId = user.cliEmpId),
        switchMap(() => this.teamsService.getTeamsList()),
        tap((teams) => {
          this.teams = teams;
          const lastViewedTeamId: string = localStorage.getItem('gp_last_viewed_team_id');
          this.selectedTeam = lastViewedTeamId ? parseInt(lastViewedTeamId, 10) : 0;
        }),
        switchMap(() => this.changeTeamObservable(this.selectedTeam, this.searchTerm))
      )
      .subscribe();
  }

  public changeDate(direction: string) {
    this.currentDate.add(direction === 'inc' ? 14 : -14, 'day');
    this.fillTable();
    this.setDateLabels();
  }

  public openCreateTeamModal(newTeamMemberId?: number) {
    this.newTeamMemberId = newTeamMemberId;
    this.teamName.setValue('');
    this.teamName.markAsPristine();

    this.modalService.openModalWithId('create-team');
  }

  public createTeam() {
    let newTeamId: number ;
    this.teamsService.createTeam({name: this.teamName.value})
      .pipe(
        switchMap((team) => {
          newTeamId = team.id;
          return this.newTeamMemberId ? this.teamsService.addContractToTeam(newTeamId, this.newTeamMemberId) : of(team);
        }),
        tap(() => this.newTeamMemberId = null),
        switchMap(() => this.teamsService.getTeamsList()),
        tap(teams => this.teams = teams),
        switchMap(() => this.changeTeamObservable(newTeamId, '')),
        finalize(() => this.modalService.closeTopmostModal())
      )
      .subscribe();
  }

  public cancelCreateTeam(createPopup) {
    this.newTeamMemberId = null;
    createPopup.close();
  }

  public deleteTeam() {
    this.teamsService.deleteTeam(this.selectedTeam)
      .pipe(
        switchMap(() => this.teamsService.getTeamsList()),
        tap((teams) => this.teams = teams)
      )
      .subscribe(() => this.changeTeamObservable(0, this.searchTerm));
  }

  public closeActiveMenu() {
    if (this.activeMenu) {
      this.activeMenu.destroy();
    }
  }

  public onMenuClick(data: { employee: CliEmpAttributes, event: MouseEvent }) {
    this.closeActiveMenu();

    setTimeout(() => {
      this.activeMenu = this.viewContainer.createEmbeddedView(this.submenuTpl, {$implicit: data.employee});
      const nativeList = this.activeMenu.rootNodes.filter(node => node.nodeName === 'UL')[0];
      nativeList.style.top = `${data.event.pageY}px`;
      nativeList.style.left = `${data.event.pageX}px`;
    });
  }

  public addContractToTeam(contractId, team) {
    this.teamsService.addContractToTeam(team.id, contractId)
      .subscribe(() => this.changeTeamObservable(this.selectedTeam, this.searchTerm));
  }

  public removeContractFromTeam(teamId, contractId) {
    this.teamsService.removeContractFromTeam(teamId, contractId)
      .subscribe(() => this.changeTeamObservable(this.selectedTeam, this.searchTerm));
  }

  public filterTeam(searchTerm: string) {
    this.searchTerm = searchTerm;
    if (!this.isCustomTeam) {
      this.changeTeam(this.selectedTeam, searchTerm);
    }
  }

  public changeTeam(event, saerchTeam: string) {
    this.changeTeamObservable(event, saerchTeam).subscribe();
  }

  private changeTeamObservable(event, searchTerm: string): Observable<Array<Data<CliEmpAttributes>>> {
    let teamList: Observable<Array<Data<CliEmpAttributes>>>;

    switch (event) {
      case 0 : {
        this.isCustomTeam = false;
        teamList = this.getDefaultTeam(searchTerm);
        break;
      }
      case 1 : {
        this.isCustomTeam = false;
        teamList = this.getIndirectSubordinatesTeam(searchTerm);
        break;
      }
      case 2 : {
        this.isCustomTeam = false;
        teamList = this.getFutureSubordinatesTeam(searchTerm);
        break;
      }
      case 3 : {
        this.isCustomTeam = false;
        teamList = this.getBackupSubordinates(searchTerm);
        break;
      }
      case 4 : {
        this.isCustomTeam = false;
        teamList = this.getAllSubordinates(searchTerm);
        break;
      }
      default : {
        this.isCustomTeam = true;
        teamList = this.getUserTeam(event);
        break;
      }
    }

    localStorage.setItem('gp_last_viewed_team_id', event.toString());

    this.selectedTeam = event;

     return teamList.pipe(
       tap(result => {
         this.employees = result.flatMap(employee => employee.empContracts.map(contract => {
           return {...employee, empContracts: [contract]};
         }));
         this.fillTable();
       }),
     );
  }

  public declareNoShow(employee: Data<CliEmpAttributes>) {
    this.currentEmployee = employee;
    this.workcycleMap.clear();
    this.initAbsenceWindow();
    this.onChangeDates();

    this.modalService.openModalWithId('edit-event');
  }

  public displayDeclareNoShowMenu(employee: any) {
    const mainContract = employee.empContracts.find(contract => contract && contract.mainContract);
    return mainContract && mainContract.subordinate && employee.confidential !== true;
  }

  public onChangeDates() {
    if (!this.currentEmployee) {
      return;
    }

    if (!this.isInWorkCycle()) {
      this.getWorkCycleForSingleEmp({
        begin: this.absenceEvent.begin,
        end: this.absenceEvent.end
      })
        .subscribe(() => this.onChangeDates());
      return;
    }

    this.isSliderLoaded = true;
    this.isBeginBeforeEnd = moment(this.absenceEvent.begin)
      .isSameOrBefore(this.absenceEvent.end);

    setTimeout(() => {
      this.isSliderLoaded = false;
    });

    this.setDateRangeTime('endDateRange', this.endDateRange, 'end');
  }

  public getRangeStep(type: string): number {
    const day = this.workcycleMap.get(this.absenceEvent[type]);

    return day && day.attributes.rangeStep || 0;
  }

  public isSingleDay(): boolean {
    return this.absenceEvent.begin === this.absenceEvent.end;
  }

  public checkDateRange(field: string, type: string) {
    if (!this.timeFields[field]) {
      return;
    }

    if (this.timeFields[field].minutes === '') {
      this.timeFields[field].minutes = 0;
    }

    if (this.timeFields[field].hours === '') {
      this.timeFields[field].hours = 0;
    }

    const minutes = this.timeFields[field].minutes / 60;
    const hours = (+this.timeFields[field].hours + +minutes);

    if (hours < 0.25) {
      this.timeFields[field].hours = 0;
      this.timeFields[field].minutes = 15;
      this.setDateRange(field, this.timeFields[field].hours, this.timeFields[field].minutes, type);
    }
  }

  public setDateRange(field: string, hours: number, minutes: number, type: string, isReverse: boolean = false): void {
    const actualDate: string = this.absenceEvent[type];

    if (!this.workcycleMap.get(actualDate)) {
      return;
    }

    const workedHours = this.workcycleMap.get(actualDate).attributes.hoursWorkedRef;

    this.setTimeFields(field, minutes, hours);

    minutes = minutes / 60;
    hours = (+hours + +minutes);

    if (this.timeFields[field].minutes === '' || this.timeFields[field].hours === '') {
      return;
    }

    if (hours > workedHours) {
      setTimeout(() => {
        this.timeFields[field].hours = workedHours;
        this.timeFields[field].minutes = 0;
        this.setDateRange(field, this.timeFields[field].hours, this.timeFields[field].minutes, type);
      });

      return;
    }

    if (hours < 0) {
      setTimeout(() => {
        this.timeFields[field].hours = 0;
        this.timeFields[field].minutes = 15;
        this.setDateRange(field, this.timeFields[field].hours, this.timeFields[field].minutes, type);
      });

      return;
    }

    if (minutes < 0) {
      setTimeout(() => {
        this.timeFields[field].minutes = 0;
        this.setDateRange(field, this.timeFields[field].hours, this.timeFields[field].minutes, type);
      });

      return;
    }

    if (minutes > 1) {
      setTimeout(() => {
        this.timeFields[field].minutes = 59;
        this.setDateRange(field, this.timeFields[field].hours, this.timeFields[field].minutes, type);
      });

      return;
    }

    hours = hours / workedHours;

    const dateRange = this.absenceEvent[field].slice();

    if (isReverse || (dateRange[1] === 1 && dateRange[0] !== 0)) {
      dateRange[0] = parseFloat((dateRange[0] + ((dateRange[1] - dateRange[0]) - hours)).toFixed(5));
    } else {
      dateRange[1] = parseFloat((dateRange[1] - ((dateRange[1] - dateRange[0]) - hours)).toFixed(5));
      if (dateRange[1] > 1) {
        dateRange[0] = parseFloat((dateRange[0] - (dateRange[1] - 1)).toFixed(5));
        dateRange[1] = 1;
      }
    }

    this.absenceEvent[field] = dateRange;
  }

  public setTimeFields(field: string, minutes: number, hours: number) {
    if (!this.timeFields[field]) {
      return;
    }

    if (+this.timeFields[field].hours !== +hours) {
      this.timeFields[field].hours = hours;
    }

    if (+this.timeFields[field].minutes !== +minutes) {
      this.timeFields[field].minutes = minutes;
    }
  }

  public setDateRangeTime(field: string, dateRange: any, type: string): void {
    const date: string = this.absenceEvent[type];

    if (!this.workcycleMap.get(date)) {
      return;
    }

    const workedHours = this.workcycleMap.get(date).attributes.hoursWorkedRef;
    this.absenceEvent[field] = dateRange;
    let minutes = Math.round((dateRange[1] - dateRange[0]) * workedHours * 60);

    const hours: number = Math.floor(minutes / 60);
    minutes = Math.round(minutes - hours * 60);

    if (+this.timeFields[field].hours !== +hours) {
      this.timeFields[field].hours = hours;
    }

    if (+this.timeFields[field].minutes !== +minutes) {
      this.timeFields[field].minutes = minutes;
    }

  }

  public sendAbsence() {
    const {id: cliEmpId, firstName, lastName} = this.currentEmployee;
    const {begin: beginDate, end: endDate} = this.absenceEvent;
    const createParams: any = {
      cliEmpId,
      firstName,
      lastName,
      beginDate,
      endDate,
      contractId: this.currentEmployee.empContracts[0].id,
      empAbsenceId: null,
      isHr: false,
      name: 'UNJ',
      comment: '',
      limitValue: null,
      transId: 'Unjustified absence',
      pendingEntityId: null
    };
    const getParams = {
      ids: this.employees.map(item => item.empContracts[0].id),
      periodBegin: moment(this.currentDate).startOf('week').add(-7, 'day').format('YYYY-MM-DD'),
      periodEnd: moment(this.currentDate).startOf('week').add(6, 'day').format('YYYY-MM-DD'),
      profileMode: this.profileMode || ProfileMode.MSS
    };

    if (this.isSingleDay()) {
      createParams.beginDateFactor = this.absenceEvent.singleDateRange[0].toFixed(5);
      createParams.endDateFactor = (1 - this.absenceEvent.singleDateRange[1]).toFixed(5);
    } else {
      createParams.beginDateFactor = this.absenceEvent.beginDateRange[0].toFixed(5);
      createParams.endDateFactor = (1 - this.absenceEvent.endDateRange[1]).toFixed(5);
    }

    const createAbsence$: Observable<any> =
      this.timeAndAttendenceService.triggerAbsenceWorkflowV2(createParams, this.currentEmployee.cliRegID, 'CREATE');

    createAbsence$.pipe(
      switchMap(() => this.getAbsences(getParams)),
      finalize(() => {
        this.modalService.closeTopmostModal();
        this.toastrService.success(this.translate.instant('WORKFLOWS.workflow_started'));
      })
    )
      .subscribe(() => {
        this.currentEmployee = null;
        this.calculateAvailability();
      });
  }

  private initAbsenceWindow() {
    this.absenceEvent = {
      beginDateRange: [0, 1],
      endDateRange: [0, 1],
      singleDateRange: [0, 1],
      begin: moment().format('YYYY-MM-DD'),
      end: moment().format('YYYY-MM-DD')
    };

    this.timeFields = {
      singleDateRange: {
        minutes: 0,
        hours: 0
      },
      beginDateRange: {
        minutes: 0,
        hours: 0
      },
      endDateRange: {
        minutes: 0,
        hours: 0
      }
    };
  }

  private createForm() {
    this.eventForm = this.formBuilder.group({
      beginDate: ['', GpValidators.validateISO8601Date],
      endDate: ['', GpValidators.validateISO8601Date],
      type: ['', ''],
      beginSlider: ['', ''],
      endSlider: ['', ''],
      duration: ['', ''],
      value: ['', ''],
      singleHours: ['', ''],
      singleMinutes: ['', ''],
      beginHours: ['', ''],
      beginMinutes: ['', ''],
      endHours: ['', ''],
      endMinutes: ['', ''],
      comment: ['', '']
    });
  }

  private getDefaultTeam(searchTerm: string): Observable<Array<Data<CliEmpAttributes>>> {
    return this.teamsService.getDefaultSubordinates(searchTerm);
  }

  private getIndirectSubordinatesTeam(searchTerm: string): Observable<Array<Data<CliEmpAttributes>>> {
    return this.teamsService.getIndirectSubordinates(searchTerm);
  }

  private getFutureSubordinatesTeam(searchTerm: string): Observable<Array<Data<CliEmpAttributes>>> {
    return this.teamsService.getFutureSubordinates(searchTerm);
  }

  private getBackupSubordinates(searchTerm: string): Observable<Array<Data<CliEmpAttributes>>> {
    return this.teamsService.getBackupSubordinates(searchTerm);
  }

  private getAllSubordinates(searchTerm: string): Observable<Array<Data<CliEmpAttributes>>> {
    return this.teamsService.getAllSubordinates(searchTerm);
  }


  private getUserTeam(teamId: number): Observable<Array<Data<CliEmpAttributes>>> {
    return this.teamsService.getTeamMembers(teamId, this.toggleSeeInactiveContracts);
  }

  private fillTable() {
    const periodBegin: string = moment(this.currentDate).startOf('week').add(-7, 'day').format('YYYY-MM-DD');
    const periodEnd: string = moment(this.currentDate).startOf('week').add(6, 'day').format('YYYY-MM-DD');
    const params = {
      ids: this.employees.map(item => item.empContracts[0].id),
      periodBegin,
      periodEnd,
      profileMode: this.profileMode || ProfileMode.MSS
    };

    this.isLoading = true;
    this.teamAvalabilityMap.clear();

    this.getWorkcycle(params)
      .pipe(
        switchMap(() => this.getAbsences(params)),
        finalize(() => {
          this.dataLoaded.emit(null);
          this.isLoading = false;
        })
      )
      .subscribe(() => {
        this.calculateAvailability();
      });
  }

  private calculateAvailability() {
    this.availablityValues = Array.from(this.teamAvalabilityMap.values()).map((item) => {
      return {
        percent: (item.availPeople / item.totalPeople) * 100,
        people: item.availPeople
      };
    });
  }

  private getWorkcycle(params): Observable<MappedDayWrapper[][]> {
    return this.teamsService.getWorkCycles(params)
      .pipe(
        map((result: { data: DayWrapper[] }[]) => {
          return result.map((item) => {

            return item.data.map((day) => {
              const dayAttrs: PayCalendarDayAttributes = day.attributes;
              const label: string = dayAttrs.activityTransId ? this.translate.instant(dayAttrs.activityTransId) : '';
              let dayData: MappedDayWrapper;

              this.appendDayToAvailMap(dayAttrs);

              switch (day.attributes.typeTransId) {
                case 'OPE':
                case 'WOR':
                  const isPartialTime = dayAttrs.hoursWorked === 0;
                  dayData = {
                    ...dayAttrs,
                    color: isPartialTime ? '#3a87ad' : '#ffffff',
                    textColor: isPartialTime ? '#ffffff' : '#909497',
                    label: isPartialTime ? this.translate.instant('PAYROLL_PREPARE.day_not_worked') : ''
                  };
                  break;
                case 'SUN':
                case 'SAT':
                case 'BAN':
                case 'OFF':
                  dayData = Object.assign(dayAttrs, {color: '#303f9f', textColor: '#ffffff', label});
                  break;
                default:
                  dayData = Object.assign(dayAttrs, {color: 'red', textColor: 'black', label});
                  break;
              }

              return dayData;
            });
          });
        }),
        tap(result => this.workCycle = result)
      );
  }

  private getAbsences(params): Observable<any> {
    return forkJoin([
      this.teamsService.getAbsences(params),
      this.teamsService.getPendingAbsences(params)
    ])
      .pipe(
        map((result: any) => {
          const mappedPendingAbsences = result[1].map(item => {
            return {
              data: item.map(pendingAbsence => Object.assign({}, pendingAbsence, {attributes: pendingAbsence}))
            };
          });

          const allAbsences = [...result[0], ...mappedPendingAbsences];

          this.absenceMap.clear();
          return allAbsences.map((absencePeriod) => {

            return absencePeriod.data.map((absence) => {
              const view: any = {
                color: '#d4efdf',
                borderColor: '',
                borderStyle: 'solid',
                readOnly: true,
                transId: absence.attributes.transId || absence.attributes.transid
              };
              let borderColor: string = '';

              switch (absence.attributes.status) {
                case 'CREATE':
                  borderColor = '#6cc788';
                  break;
                case 'UPDATE':
                  borderColor = '#FCE605';
                  break;
                case 'CANCELLATION':
                  borderColor = '#f44455';
                  break;
                default:
                  break;
              }

              view.borderColor = borderColor;

              const mappedAbsence = Object.assign({view}, absence);

              Object.assign(mappedAbsence.attributes, {
                empContract: absence.attributes.empContract || absence.attributes.empContractId,
                begin: absence.attributes.beginDate || absence.attributes.begin,
                end: absence.attributes.endDate || absence.attributes.end
              });

              this.appendAbsenceToAvailMap(mappedAbsence);

              return mappedAbsence;
            });

          });
        })
      );
  }

  private setDateLabels() {
    let firstDate: string = moment(this.currentDate).startOf('week').add(-7, 'day').format('MMM');
    let lastDate: string = moment(this.currentDate).startOf('week').add(-1, 'day').format('MMM');

    this.monthRange1Week = firstDate === lastDate ? firstDate : `${firstDate} - ${lastDate}`;
    this.week1Num = weekOfYear(moment(this.currentDate).startOf('week').add(-7, 'day'));

    firstDate = moment(this.currentDate).startOf('week').format('MMM');
    lastDate = moment(this.currentDate).startOf('week').add(6, 'day').format('MMM');

    this.monthRange2Week = firstDate === lastDate ? firstDate : `${firstDate} - ${lastDate}`;

    this.dayLabels = [-7, -6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6].map((num) => {
      return moment(this.currentDate).startOf('week').add(num, 'day').format('DD ddd');
    });
    this.week2Num = weekOfYear(moment(this.currentDate).startOf('week'));
  }

  private appendDayToAvailMap(dayAttrs) {
    const dayTotal = this.teamAvalabilityMap.get(dayAttrs.date)
      || {workedHours: 0, absenceHours: 0, availPeople: 0, totalPeople: 0};
    dayTotal.workedHours += dayAttrs.hoursWorkedRef;
    dayTotal.totalPeople++;
    if (dayAttrs.hoursWorked > 0) {
      dayTotal.availPeople++;
    }
    this.teamAvalabilityMap.set(dayAttrs.date, dayTotal);
  }

  private appendAbsenceToAvailMap(absence) {
    const absenceAttrs = absence.attributes;

    this.teamAvalabilityMap.forEach((value, key) => {
      const employee = this.employees.find(emp => emp.empContracts[0].id === absenceAttrs.empContract);
      const workCycle: MappedDayWrapper[] = this.workCycle[this.employees.indexOf(employee)];
      const workDay: MappedDayWrapper = workCycle.find((day) => day.date === key);

      if (!workDay.hoursWorkedRef) {
        return;
      }

      if (absenceAttrs.begin === absenceAttrs.end && absenceAttrs.begin === key) {
        const sumPercent: number = absenceAttrs.beginDayfactor + absenceAttrs.endDayfactor;

        value.absenceHours += workDay.hoursWorkedRef * (1 - sumPercent);
        value.availPeople -= ((workDay.hoursWorkedRef * (1 - sumPercent)) / workDay.hoursWorkedRef);
        this.setAbsenceMap(key, employee.id, absence);

      } else {

        if (key === absenceAttrs.begin) {
          value.absenceHours += workDay.hoursWorkedRef * (1 - absenceAttrs.beginDayfactor);
          value.availPeople -= ((workDay.hoursWorkedRef * (1 - absenceAttrs.beginDayfactor)) / workDay.hoursWorkedRef);
          this.setAbsenceMap(key, employee.id, absence);
        }

        if (key === absenceAttrs.end) {
          value.absenceHours += workDay.hoursWorkedRef * (1 - absenceAttrs.endDayfactor);
          value.availPeople -= ((workDay.hoursWorkedRef * (1 - absenceAttrs.endDayfactor)) / workDay.hoursWorkedRef);
          this.setAbsenceMap(key, employee.id, absence);
        }

        if (moment(key).isBefore(absenceAttrs.end) && moment(key).isAfter(absenceAttrs.begin)) {
          value.absenceHours += workDay.hoursWorkedRef;
          value.availPeople--;
          this.setAbsenceMap(key, employee.id, absence);
        }

      }
    });

  }

  private setAbsenceMap(key: string, employeeId: number, absence) {
    const absenceList = this.absenceMap.get(`${key}-${employeeId}`);
    if (absenceList) {
      absenceList.push(absence);
    } else {
      this.absenceMap.set(`${key}-${employeeId}`, [absence]);
    }
  }

  private isInWorkCycle() {
    let isNotExist: boolean = false;
    range(
      this.absenceEvent.begin,
      this.absenceEvent.end
    ).by('days', (momentDay) => {
      if (!this.workcycleMap.has(momentDay.format(DATE_FORMAT))) {
        isNotExist = true;
      }
    });
    return !isNotExist;
  }

  private getWorkCycleForSingleEmp(dateRange: DateRange) {

    return this.timeAndAttendenceService
      .getWorkCycle(this.currentEmployee.empContracts[0].id, dateRange.begin, dateRange.end, this.profileMode)
      .pipe(
        map(result => {
          result.data.forEach((item) => {
            if (item.attributes.hoursWorkedRef) {
              item.attributes.rangeStep = 1 / item.attributes.hoursWorkedRef;
              item.attributes.isEmptyHours = false;
            } else {
              item.attributes.isEmptyHours = true;
              item.attributes.rangeStep = 0;
            }

            this.workcycleMap.set(item.attributes.date, item);
          });
          return result.data;
        })
      );
  }

}
